import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

export const SIDEBAR_WIDTH = 248;
export const COLLAPSED_WIDTH = 72;

const STORAGE_KEY_COLLAPSED = 'munifocus-gov-sidenav-collapsed';

interface SidenavContextValue {
  /** Desktop: the rail is collapsed to an icon strip. */
  isCollapsed: boolean;
  /** The rail's current width in pixels, for offsetting the content column. */
  width: number;
  toggleCollapse: () => void;
}

const SidenavContext = createContext<SidenavContextValue | null>(null);

function readCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(STORAGE_KEY_COLLAPSED) === 'true';
}

export function SidenavProvider({ children }: { children: ReactNode }) {
  const [isCollapsed, setIsCollapsed] = useState(readCollapsed);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY_COLLAPSED, String(isCollapsed));
  }, [isCollapsed]);

  const toggleCollapse = useCallback(() => setIsCollapsed((prev) => !prev), []);

  return (
    <SidenavContext.Provider
      value={{
        isCollapsed,
        width: isCollapsed ? COLLAPSED_WIDTH : SIDEBAR_WIDTH,
        toggleCollapse,
      }}
    >
      {children}
    </SidenavContext.Provider>
  );
}

export function useSidenav(): SidenavContextValue {
  const context = useContext(SidenavContext);
  if (!context) {
    throw new Error('useSidenav must be used within a SidenavProvider');
  }
  return context;
}
